import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useSavedFeedbackListStore } from '@modules/savedFeedbackList/store/useSavedFeedbackListStore';
import { useContext } from 'react';
import { FieldNames } from 'v2/core/entities/fields';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { getWhereClauseForFieldName } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { useGetUser } from 'v2/core/services/user';
import { SaveFeedbackContext } from '../SaveFeedbackContext';

interface UseAddFeedbackToCollectionProps {
  feedbackID: string;
  collectionEditToken?: string;
  collectionEQL: string;
  collectionID: string;
}

export const useAddFeedbackToCollection = ({
  feedbackID,
  collectionEditToken,
  collectionEQL,
  collectionID,
}: UseAddFeedbackToCollectionProps) => {
  const {
    tenant: { id: tenantID, timezone },
  } = useGetUser();
  const { data } = useGetSourceDetailsMap();

  const { savedItemsMap } = useSavedFeedbackListStore();

  const contextData = useContext(SaveFeedbackContext);

  const existingFeedbackIDsOfCollection =
    (data &&
      parseEQL(collectionEQL, data, timezone).whereClause?.filter?.idFilter
        ?.ids) ??
    [];

  const updatedFeedbackIDsOfCollection = [
    ...existingFeedbackIDsOfCollection,
    feedbackID,
  ].filter(Boolean);

  const updatedWhereClause = getWhereClauseForFieldName({
    fieldName: FieldNames.ID,
    values: updatedFeedbackIDsOfCollection,
    op: OperationType.AND,
  });

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: updatedWhereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const { updateAnalysis, isLoading } = useUpdateAnalysis({});

  const addFeedbackToCollection = async () => {
    if (!collectionEditToken || !eql) {
      return;
    }

    const isSnippetMetadataPresent =
      contextData?.snippetID && contextData.labelID;

    if (isSnippetMetadataPresent) {
      await updateAnalysis({
        variables: {
          id: collectionID,
          tenantID: tenantID,
          editToken: collectionEditToken,
          context: {
            eqlExpression: eql,
          },
          metadataV2: {
            collection: {
              savedFeedbackMetadata: [
                {
                  feedbackID: feedbackID,
                  snippetMetadataV2: [
                    ...(savedItemsMap[feedbackID]?.snippetMetadataV2 ?? []),
                    {
                      location: {
                        keywordSummary: {
                          startIdx: 0,
                          endIdx: contextData?.snippetID.length ?? 0,
                          msgIdx: contextData.summaryIndex,
                          labelID: contextData.labelID,
                        },
                      },
                    },
                  ],
                },
              ],
            },
          },
        },
      });
    } else {
      await updateAnalysis({
        variables: {
          id: collectionID,
          tenantID: tenantID,
          editToken: collectionEditToken,
          context: {
            eqlExpression: eql,
          },
        },
      });
    }
  };

  return {
    addFeedbackToCollection,
    isLoading,
  };
};
